import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getPhotowallMedia } from '@xoxokiosk/shared';
import styles from './PhotowallPage.module.css';

interface PhotowallItem {
  id: string;
  videoUrl: string; // Can be either video URL or image URL
  mbtiType: string;
  userName: string;
  drinkName: string;
  createdAt: string;
}

const PhotowallPage: React.FC = () => {
  const { shopId } = useParams<{ shopId: string }>();
  const navigate = useNavigate();
  const [photowallItems, setPhotowallItems] = useState<PhotowallItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedItem, setSelectedItem] = useState<PhotowallItem | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState<Date>(new Date());

  const fetchPhotowallItems = async (isManualRefresh = false) => {
    try {
      // Only show full loading state for initial load, not for manual refresh
      if (!isManualRefresh) {
        setLoading(true);
      } else {
        setIsRefreshing(true);
      }
      setError(null);
      console.log('Fetching photowall items for shopId:', shopId);
      
      if (!shopId) {
        throw new Error('Shop ID is required');
      }
      
      const items = await getPhotowallMedia(shopId, 50);
      console.log('Received items:', items);
      setPhotowallItems(items);
      setLastRefreshTime(new Date());
    } catch (err: any) {
      console.error('Error fetching photowall items:', err);
      setError('Unable to load photowall');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  };

  const handleManualRefresh = () => {
    fetchPhotowallItems(true);
  };

  useEffect(() => {
    if (shopId) {
      fetchPhotowallItems();
    }
  }, [shopId]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (shopId) {
        fetchPhotowallItems(false); // Auto-refresh is not manual
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [shopId]);

  const isVideo = (url: string) => {
  return url.match(/\.(mp4|webm|ogg|mov|avi)$/i) !== null;
};

const isImage = (url: string) => {
  return url.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i) !== null;
};

const getMBTIColor = (mbtiType: string) => {
    const colors: Record<string, string> = {
      'INTJ': '#6366f1', 'INTP': '#8b5cf6', 'ENTJ': '#ef4444', 'ENTP': '#f97316',
      'INFJ': '#06b6d4', 'INFP': '#14b8a6', 'ENFJ': '#10b981', 'ENFP': '#84cc16',
      'ISTJ': '#64748b', 'ISFJ': '#f59e0b', 'ESTJ': '#eab308', 'ESFJ': '#fbbf24',
      'ISTP': '#94a3b8', 'ISFP': '#fda4af', 'ESTP': '#fb923c', 'ESFP': '#fb7185'
    };
    return colors[mbtiType.toUpperCase()] || '#6b7280';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatLastRefreshTime = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    
    if (diffSecs < 60) return 'just now';
    if (diffSecs < 3600) return `${Math.floor(diffSecs / 60)}m ago`;
    return `${Math.floor(diffSecs / 3600)}h ago`;
  };

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <button className={styles.backButton} onClick={() => navigate(-1)}>
            ← Back
          </button>
          <h1>Guest Photowall</h1>
          <div className={styles.spacer} />
        </div>
        <div className={styles.loadingGrid}>
          {[...Array(12)].map((_, i) => (
            <div key={i} className={styles.skeletonCard} />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <button className={styles.backButton} onClick={() => navigate(-1)}>
            ← Back
          </button>
          <h1>Guest Photowall</h1>
          <div className={styles.spacer} />
        </div>
        <div className={styles.errorState}>
          <div className={styles.errorIcon}>❌</div>
          <p>Unable to load photowall</p>
          <p className={styles.errorDetails}>Check browser console for details</p>
          <button className={styles.retryButton} onClick={() => fetchPhotowallItems(true)}>
            Try Again
          </button>
          <button className={styles.testButton} onClick={() => window.open(`http://localhost:8080/api/mbti/photowall/test?shopId=${shopId}`, '_blank')}>
            Test API
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={() => navigate(-1)}>
          ← Back
        </button>
        <h1>Guest Photowall</h1>
        <div className={styles.headerControls}>
          <button 
            className={`${styles.refreshButton} ${isRefreshing ? styles.refreshing : ''}`}
            onClick={handleManualRefresh}
            disabled={isRefreshing}
            title="Refresh photowall"
          >
            {isRefreshing ? '⟳' : '↻'}
          </button>
          <div className={styles.photoCount}>
            {photowallItems.length} photos
          </div>
        </div>
      </div>
      
      {!loading && !error && (
        <div className={styles.refreshStatus}>
          Last updated: {formatLastRefreshTime(lastRefreshTime)}
        </div>
      )}

      {photowallItems.length === 0 ? (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>📸</div>
          <h2>No photos yet</h2>
          <p>When guests choose to share their Travel3 creations, they'll appear here</p>
        </div>
      ) : (
        <div className={styles.photowallContainer}>
          {isRefreshing && (
            <div className={styles.refreshOverlay}>
              <div className={styles.refreshSpinner}>⟳</div>
              <p>Refreshing...</p>
            </div>
          )}
          <div className={styles.photowallGrid}>
            {photowallItems.map((item) => (
              <div 
                key={item.id} 
                className={styles.photowallCard}
                onClick={() => setSelectedItem(item)}
              >
                <div className={styles.imageContainer}>
                  {item.videoUrl && isVideo(item.videoUrl) ? (
                    <video
                      className={styles.photowallVideo}
                      src={item.videoUrl}
                      autoPlay
                      muted
                      loop
                      playsInline
                    />
                  ) : item.videoUrl && isImage(item.videoUrl) ? (
                    <img
                      className={styles.photowallImage}
                      src={item.videoUrl}
                      alt="Photowall item"
                    />
                  ) : (
                    <div className={styles.placeholderImage}>
                      <div 
                        className={styles.mbtiBadge}
                        style={{ backgroundColor: getMBTIColor(item.mbtiType) }}
                      >
                        {item.mbtiType}
                      </div>
                    </div>
                  )}
                </div>
                <div className={styles.photowallInfo}>
                  <span className={styles.userName}>{item.userName}</span>
                  <span 
                    className={styles.mbtiType}
                    style={{ color: getMBTIColor(item.mbtiType) }}
                  >
                    {item.mbtiType}
                  </span>
                </div>
                <div className={styles.timestamp}>
                  {formatDate(item.createdAt)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal for selected item */}
      {selectedItem && (
        <div className={styles.modal} onClick={() => setSelectedItem(null)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <button className={styles.closeButton} onClick={() => setSelectedItem(null)}>
              ×
            </button>
            <div className={styles.modalImage}>
              {selectedItem.videoUrl && isVideo(selectedItem.videoUrl) ? (
                <video
                  src={selectedItem.videoUrl}
                  autoPlay
                  muted
                  loop
                  playsInline
                  controls
                />
              ) : selectedItem.videoUrl && isImage(selectedItem.videoUrl) ? (
                <img
                  src={selectedItem.videoUrl}
                  alt="Photowall item"
                  style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
                />
              ) : (
                <div className={styles.modalPlaceholder}>
                  <div 
                    className={styles.modalMbtiBadge}
                    style={{ backgroundColor: getMBTIColor(selectedItem.mbtiType) }}
                  >
                    {selectedItem.mbtiType}
                  </div>
                </div>
              )}
            </div>
            <div className={styles.modalInfo}>
              <h3>{selectedItem.userName}</h3>
              <p className={styles.modalDrinkName}>
                🍹 {selectedItem.drinkName}
              </p>
              <p 
                className={styles.modalMbtiType}
                style={{ color: getMBTIColor(selectedItem.mbtiType) }}
              >
                {selectedItem.mbtiType}
              </p>
              <p className={styles.modalTimestamp}>
                {formatDate(selectedItem.createdAt)}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotowallPage;
